import { z } from "zod";
import { idSchema, instantSchema, readinessSchema } from "@amber/contracts";
import { reportedProblemKindSchema, type REPORTED_PROBLEM_KINDS } from "./corrections.js";
import { carriedSummarySchema } from "./copies.js";

export const EDITING_ACTIONS = {
  correct: "evidence.correct",
  report: "evidence.report_problem",
  resolve: "evidence.resolve_problem",
} as const;

export const EDITOR_ONLY_REASON = "Only Editors and above can correct evidence and report problems";

/** Edit lease lifetime in ms, initial value from the edit-lease decision (2026-10-11). */
export const LEASE_DURATION_MS = 120_000;
/** Heartbeat interval in ms that extends a held lease, from the same decision. */
export const LEASE_HEARTBEAT_MS = 20_000;

export const WORD_REASON_CATEGORIES = ["Misheard name", "Wrong number", "Misheard word", "Spelling"] as const;

export const PROBLEM_KIND_LABELS: Record<(typeof REPORTED_PROBLEM_KINDS)[number], string> = {
  wrong_text: "Wrong text",
  wrong_speaker: "Wrong speaker",
  wrong_timing: "Wrong timing",
  context: "Context",
  other: "Other",
};

export const leaseViewSchema = z.strictObject({
  lease_id: idSchema("lse"),
  communication_id: idSchema("com"),
  revision_id: idSchema("rev"),
  expires_at: instantSchema,
  duration_ms: z.number().int().positive(),
  heartbeat_ms: z.number().int().positive(),
});
export type LeaseView = z.infer<typeof leaseViewSchema>;

export const leaseReleaseSchema = z.strictObject({ state: z.enum(["released", "expired", "not_held"]) });

export const correctionChangeSchema = z.discriminatedUnion("kind", [
  z.strictObject({
    kind: z.literal("word"),
    position: z.number().int().nonnegative(),
    text: z.string().trim().min(1).regex(/^\S+$/, "A word has no spaces; correct the turn instead"),
  }),
  z.strictObject({ kind: z.literal("turn_text"), text: z.string().trim().min(1, "The turn needs some text") }),
  z.strictObject({ kind: z.literal("speaker"), speaker_id: idSchema("spk") }),
]);
export type CorrectionChange = z.infer<typeof correctionChangeSchema>;

export const correctionRequestSchema = z.strictObject({
  lease_id: idSchema("lse"),
  base_revision_id: idSchema("rev"),
  unit_id: idSchema("unt"),
  reason: z.string().trim().min(1, "A reason is required"),
  changes: z.array(correctionChangeSchema).min(1),
  resolves: z.array(idSchema("rpt")).default([]),
});
export type CorrectionRequest = z.input<typeof correctionRequestSchema>;

export const correctionResponseSchema = z.strictObject({
  revision_id: idSchema("rev"),
  version: z.number().int().positive(),
  unit_id: idSchema("unt"),
  corrections: z.array(idSchema("cor")),
  resolved: z.array(idSchema("rpt")),
  readiness: z.strictObject({ result: readinessSchema, reason: z.string().nullable() }).nullable(),
  carried: z.array(carriedSummarySchema),
});
export type CorrectionResponse = z.infer<typeof correctionResponseSchema>;

export const turnConflictSchema = z.strictObject({
  revision_id: idSchema("rev"),
  unit_id: idSchema("unt").nullable(),
  text: z.string().nullable(),
  speaker_id: idSchema("spk").nullable(),
});
export type TurnConflict = z.infer<typeof turnConflictSchema>;

export const problemScopeRequestSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("item") }),
  z.strictObject({ kind: z.literal("communication"), communication_id: idSchema("com") }),
  z.strictObject({
    kind: z.literal("passage"),
    communication_id: idSchema("com"),
    unit_ids: z.array(idSchema("unt")).min(1),
  }),
]);

export const reportProblemRequestSchema = z.strictObject({
  scope: problemScopeRequestSchema,
  kind: reportedProblemKindSchema,
  description: z.string().trim().min(1, "Describe the problem"),
});
export type ReportProblemRequest = z.input<typeof reportProblemRequestSchema>;

export const resolveProblemRequestSchema = z.strictObject({ note: z.string().trim().min(1, "A note is required") });

export const problemResponseSchema = z.strictObject({
  id: idSchema("rpt"),
  state: z.enum(["open", "resolved"]),
  task_id: idSchema("tsk").nullable(),
});
